'use client'

import { useState, type ReactNode } from 'react'
import Image from 'next/image'
import logo from '@/public/logo.png'
import { IconChevronDown, IconMessagePlus } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { WithTooltip } from '@/components/ui/with-tooltip'
import { PublicLoginPrompt } from './public-login-prompt'

interface PublicChatHeaderProps {
  authAction?: ReactNode
  onModelUpgrade?: () => void
  onNewChat?: () => void
  showAuthActions?: boolean
}

export function PublicChatHeader({
  authAction,
  onModelUpgrade,
  onNewChat,
  showAuthActions = true
}: PublicChatHeaderProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [isHoveringLogo, setIsHoveringLogo] = useState(false)

  const handleLogin = () => {
    router.push('/login?next=try')
  }

  const handleSignup = () => {
    router.push('/login?register=true&next=try')
  }

  return (
    <>
      <div className="sticky top-2 z-20 flex h-14 w-full shrink-0 items-center justify-between p-4">
        {/* Left side - Brand and Model */}
        <div className="border-input flex h-10 items-center justify-center rounded-full border bg-slate-50/10 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-slate-50/70 hover:shadow-md hover:backdrop-blur-2xl dark:bg-neutral-900/60 dark:backdrop-blur-sm hover:dark:bg-neutral-800/70">
          <WithTooltip
            display={<div>Start new chat</div>}
            side="bottom"
            delayDuration={500}
            asChild={true}
            trigger={
              <button
                type="button"
                className="focus-visible:ring-primary/40 relative flex h-full items-center rounded-l-full pl-4 pr-1 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-default disabled:opacity-60"
                onMouseEnter={() => setIsHoveringLogo(true)}
                onMouseLeave={() => setIsHoveringLogo(false)}
                onFocus={() => setIsHoveringLogo(true)}
                onBlur={() => setIsHoveringLogo(false)}
                disabled={!onNewChat}
                aria-label="Start new chat"
                onClick={() => {
                  onNewChat?.()
                }}
              >
                <AnimatePresence mode="wait">
                  {isHoveringLogo ? (
                    <motion.div
                      key="new-chat-icon"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      className="flex items-center justify-center"
                    >
                      <IconMessagePlus size={32} className="text-foreground" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="logo"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                    >
                      <Image width={32} height={32} src={logo} alt="Losi" className="rounded-sm" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            }
          />

          {/* Model Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex h-full items-center gap-1 px-3 text-sm font-medium"
                onClick={(e) => {
                  e.preventDefault()
                }}
              >
                <span>Losi Mini</span>
                <IconChevronDown size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[200px]">
              <DropdownMenuItem
                className="flex cursor-pointer items-center justify-between py-2"
                onClick={() => {
                  setShowLoginPrompt(true)
                  onModelUpgrade?.()
                }}
              >
                <div className="flex flex-col">
                  <span className="font-medium">Losi Max</span>
                  <span className="text-muted-foreground text-xs">Smarter responses</span>
                </div>
                <span className="text-muted-foreground text-xs">Log in required</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right side - CTA/Auth */}
        {authAction ? (
          <div className="flex h-10 min-w-0 items-center justify-end">
            {authAction}
          </div>
        ) : showAuthActions ? (
          <div className="flex h-10 items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleLogin}
              className="border-input hidden h-10 rounded-full border bg-slate-50/50 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-slate-50/70 hover:shadow-md hover:backdrop-blur-2xl sm:flex dark:bg-neutral-700/20 dark:backdrop-blur-sm hover:dark:bg-neutral-800/70"
            >
              {t('marketing.header.login')}
            </Button>
            <Button
              onClick={handleSignup}
              className="border-input/50 h-10 rounded-full border bg-black/60 font-medium text-white shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-black/80 hover:shadow-md hover:backdrop-blur-2xl dark:bg-white/80 dark:text-black dark:backdrop-blur-md hover:dark:bg-white/95"
            >
              Sign up for free
            </Button>
          </div>
        ) : (
          <div className="size-10" aria-hidden="true" />
        )}
      </div>

      {/* Upgrade Banner */}
      {/* <div className="bg-muted/50 border-b px-4 py-2 text-center text-sm">
        <span className="text-muted-foreground">
          Log in or sign up to use{' '}
          <button
            onClick={() => setShowLoginPrompt(true)}
            className="font-medium text-foreground underline hover:no-underline"
          >
            Losi Max
          </button>
          , a better model
        </span>
      </div> */}

      <PublicLoginPrompt open={showLoginPrompt} onOpenChange={setShowLoginPrompt} />
    </>
  )
}
